import React from 'react';
import { Link } from 'react-router-dom';
import { Download, Instagram, BookHeart } from 'lucide-react';
import { ArchetypeIllustration } from './ArchetypeIllustration';
import { StoryCard } from './StoryCard';
import { useDownloadStoryCard } from '../hooks/useDownloadStoryCard';
import { UNIVERSAL_CTA } from '../data/archetypes';

// Small helper to render a paragraph that may contain trusted <em> tags from data.
const RichText = ({ text, className, style }) => (
  <p
    className={className}
    style={style}
    dangerouslySetInnerHTML={{ __html: text }}
  />
);

export const ResultScreen = ({ archetype, firstName, onRetake }) => {
  const { ref: storyRef, download, busy } = useDownloadStoryCard();

  const handleDownload = () => {
    const key = (archetype.key || 'archetype').toLowerCase();
    download(`my-birth-archetype-${key}.png`);
  };

  return (
    <section
      data-testid="result-screen"
      className="screen-enter mx-auto w-full max-w-[680px] px-5 sm:px-8 pt-8 sm:pt-12 pb-20"
    >
      {/* Hero block */}
      <div
        className="rounded-[28px] px-6 sm:px-10 py-10 sm:py-12 text-center mb-10"
        style={{
          backgroundColor: '#B8A78F',
          color: '#3D3830',
          boxShadow: 'none',
        }}
        data-testid="result-hero"
      >
        <div
          className="inline-flex items-center justify-center mb-5 rounded-full"
          style={{ width: 72, height: 72, backgroundColor: 'rgba(255,255,255,0.35)' }}
        >
          <ArchetypeIllustration archetypeKey={archetype.key} size={52} style={{ color: '#3D3830' }} />
        </div>
        {firstName && (
          <p className="font-body text-[13px]" style={{ letterSpacing: '0.22em', textTransform: 'uppercase', opacity: 0.75, fontStyle: 'normal' }}>
            {firstName}, you are
          </p>
        )}
        <h1 className="font-heading italic text-[40px] sm:text-[52px] leading-[1.05] mt-2" style={{ fontWeight: 500 }} data-testid="result-name">
          {archetype.name}
        </h1>
        <p className="font-body text-[12px] mt-4" style={{ letterSpacing: '0.22em', textTransform: 'uppercase', opacity: 0.75, fontStyle: 'normal' }}>
          {archetype.profile}
        </p>
        <p className="font-heading italic text-[20px] sm:text-[22px] mt-5 leading-[1.4]" data-testid="result-tagline">
          &ldquo;{archetype.tagline}&rdquo;
        </p>
      </div>

      {/* Opening hook */}
      <div className="text-center mb-12" data-testid="result-hook">
        <div
          className="mx-auto mb-8 flex items-center justify-center"
          style={{ color: '#3D3830' }}
          data-testid="result-illustration"
        >
          <ArchetypeIllustration archetypeKey={archetype.key} size={148} />
        </div>
        <RichText
          text={archetype.hook}
          className="font-heading italic text-[22px] sm:text-[26px] leading-[1.4]"
          style={{ color: '#3D3830' }}
        />
      </div>

      {/* Sections */}
      <div className="flex flex-col gap-9" data-testid="result-sections">
        {archetype.sections.map((s, i) => (
          <div key={i} data-testid={`result-section-${i}`}>
            <div className="section-label mb-2">{s.label}</div>
            <RichText
              text={s.body}
              className="font-body text-[17px] leading-[1.7]"
              style={{ color: '#3D3830' }}
            />
          </div>
        ))}
      </div>

      {/* Affirmation block */}
      <div
        className="rounded-[24px] my-12 px-6 sm:px-10 py-9 text-center"
        style={{ backgroundColor: '#3D3830', color: '#E8E0D5' }}
        data-testid="result-affirmation"
      >
        <div className="font-body text-[11px] mb-3" style={{ letterSpacing: '0.28em', textTransform: 'uppercase', opacity: 0.75, fontStyle: 'normal' }}>
          Affirmation
        </div>
        <p className="font-heading italic text-[22px] sm:text-[26px] leading-[1.45]">
          &ldquo;{archetype.affirmation}&rdquo;
        </p>
      </div>

      {/* Zodiac */}
      <div className="mb-10" data-testid="result-zodiac">
        <div className="rounded-2xl p-5" style={{ backgroundColor: 'rgba(184, 167, 143, 0.35)', border: '1px solid rgba(184, 167, 143, 0.55)' }}>
          <div className="section-label mb-1">Zodiac resonance</div>
          <p className="font-heading italic text-[22px]" style={{ fontWeight: 500, color: '#3D3830' }}>{archetype.zodiac}</p>
        </div>
      </div>

      {/* Suggested next step */}
      <div className="mb-12" data-testid="result-next-step">
        <div className="section-label mb-2">Your suggested next step</div>
        <RichText
          text={archetype.nextStep}
          className="font-body text-[17px] leading-[1.7]"
          style={{ color: '#3D3830' }}
        />
      </div>

      {/* Companion toolkit CTA */}
      <div
        className="rounded-[24px] mt-2 mb-10 px-6 sm:px-8 py-7 text-center"
        style={{
          backgroundColor: 'rgba(184, 167, 143, 0.35)',
          border: '1px solid rgba(184, 167, 143, 0.55)',
        }}
        data-testid="result-toolkit"
      >
        <div className="section-label mb-3">
          <span className="inline-flex items-center gap-2">
            <BookHeart size={13} strokeWidth={1.6} /> Your companion toolkit
          </span>
        </div>
        <p className="font-heading italic text-[19px] sm:text-[22px] leading-[1.4] mb-5" style={{ color: '#3D3830' }}>
          A short printable ritual made for {archetype.name}.
        </p>
        <Link
          data-testid="result-toolkit-link"
          to={`/resource/${(archetype.key || '').toLowerCase()}`}
          target="_top"
          className="btn-primary"
          style={{ textDecoration: 'none' }}
        >
          Open my free toolkit
        </Link>
      </div>

      {/* Universal CTA block (archetype-specific opening line + universal description + button) */}
      <div
        className="rounded-[28px] px-6 sm:px-10 py-10 mb-12 text-center"
        style={{ backgroundColor: '#3D3830', color: '#E8E0D5' }}
        data-testid="result-workshop-cta"
      >
        <p className="font-heading italic text-[22px] sm:text-[26px] leading-[1.35] mb-4" style={{ color: '#E8E0D5' }}>
          {archetype.ctaOpeningLine}
        </p>
        <p className="font-body text-[16px] leading-[1.65] mx-auto max-w-[540px] mb-7" style={{ color: 'rgba(232, 224, 213, 0.88)' }}>
          {UNIVERSAL_CTA.description}
        </p>
        <a
          data-testid="result-primary-cta"
          href={UNIVERSAL_CTA.buttonUrl}
          target="_top"
          rel="noopener noreferrer"
          className="btn-primary-inverse"
          style={{ textDecoration: 'none' }}
        >
          {UNIVERSAL_CTA.buttonText}
        </a>
      </div>

      {/* Retake */}
      <div className="text-center pt-6 border-t" style={{ borderColor: 'rgba(184, 167, 143, 0.55)' }}>
        <button
          data-testid="result-retake-button"
          onClick={onRetake}
          className="btn-ghost"
        >
          Retake the quiz
        </button>
      </div>

      {/* Share / Save card */}
      <div className="text-center mt-8 mb-2" data-testid="result-share">
        <div className="section-label mb-3">
          <span className="inline-flex items-center gap-2">
            <Instagram size={13} strokeWidth={1.6} /> Share your archetype
          </span>
        </div>
        <p className="font-body text-[14px] mb-4" style={{ color: '#3D3830', opacity: 0.75 }}>
          Save a beautiful 1080×1920 story card to your phone and post it.
        </p>
        <button
          data-testid="result-save-card-button"
          onClick={handleDownload}
          disabled={busy}
          className="btn-primary"
        >
          <Download size={14} strokeWidth={1.6} />
          {busy ? 'Preparing your card…' : 'Save my archetype card'}
        </button>
      </div>

      {/* Off-screen StoryCard source for rasterization */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: -99999,
          top: 0,
          width: 1080,
          height: 1920,
          pointerEvents: 'none',
          opacity: 0,
        }}
      >
        <StoryCard ref={storyRef} archetype={archetype} />
      </div>
    </section>
  );
};
